// src/utils/PdfPositionUtils.js - PERCENTAGE-FIRST utilities
import { STAMP_DIMENSIONS } from '../components/stamp/Stamp';

/**
 * PDF Position Utilities - PERCENTAGE-FIRST APPROACH
 * 
 * New Philosophy:
 * - PDF points used ONLY for initial server-side calculations
 * - Percentages are the single source of truth for everything else
 * - Pure CSS inheritance with no coordinate system juggling
 */

export const PDF_POINTS_PER_INCH = 72;
export const DEFAULT_MARGIN_INCHES = 0.25;
export const DEFAULT_MARGIN_POINTS = DEFAULT_MARGIN_INCHES * PDF_POINTS_PER_INCH; // 18 points

// Standard US Letter page dimensions in points (for reference/export only)
export const STANDARD_PAGE_POINTS = {
  width: 612,   // 8.5 inches × 72 points/inch
  height: 792   // 11 inches × 72 points/inch
};

/**
 * PERCENTAGE-FIRST: Convert PDF points to percentages ONCE
 * This happens when:
 * 1. Loading existing stamp positions from database
 * 2. Getting initial position from server calculations
 * 
 * After this conversion, we ONLY work with percentages!
 * 
 * @param {Object} pdfPointPosition - Position in PDF page units {x, y}
 * @param {number} pageWidth - PDF page width in points
 * @param {number} pageHeight - PDF page height in points  
 * @returns {Object} Position as percentages {x, y}
 */
export const pdfPointsToPercentages = (pdfPointPosition, pageWidth, pageHeight) => {
  /**
  console.log('🔄 Converting PDF points → Percentages (ONCE ONLY):', {
    pdfPoints: pdfPointPosition,
    pageSize: { width: pageWidth, height: pageHeight }
  });
  */

  // Convert PDF coordinate system (bottom-left origin) to CSS system (top-left origin)
  const cssTop = pageHeight - pdfPointPosition.y - STAMP_DIMENSIONS.BASE_HEIGHT;
  
  // Convert to percentages
  const percentX = (pdfPointPosition.x / pageWidth) * 100;
  const percentY = (cssTop / pageHeight) * 100;
  
  // Ensure stamp stays within bounds (0-100% minus stamp size)
  const maxPercentX = 100 - (STAMP_DIMENSIONS.BASE_WIDTH / pageWidth * 100);
  const maxPercentY = 100 - (STAMP_DIMENSIONS.BASE_HEIGHT / pageHeight * 100);
  
  const clampedPercent = {
    x: Math.max(0, Math.min(percentX, maxPercentX)),
    y: Math.max(0, Math.min(percentY, maxPercentY))
  };
  
  // console.log('✅ PDF points → Percentages conversion complete:', clampedPercent);
  return clampedPercent;
};

/**
 * PERCENTAGE-FIRST: Get default stamp position as percentages
 * No PDF point calculations needed - pure percentage positioning!
 * 
 * Places stamp at bottom-right corner (CSS coordinates)
 * @returns {Object} Default position as percentages {x, y}
 */
export const getDefaultStampLocationPercent = () => {
  // Using standard page proportions for default positioning
  const marginPercent = (DEFAULT_MARGIN_POINTS / STANDARD_PAGE_POINTS.width) * 100; // ~2.9%
  const stampWidthPercent = (STAMP_DIMENSIONS.BASE_WIDTH / STANDARD_PAGE_POINTS.width) * 100; // ~15.7%
  const stampHeightPercent = (STAMP_DIMENSIONS.BASE_HEIGHT / STANDARD_PAGE_POINTS.height) * 100; // ~6.8%
  
  // Position at bottom-right with margin
  const defaultPercent = {
    x: 100 - stampWidthPercent - marginPercent, // ~81.4%
    y: 100 - stampHeightPercent - marginPercent  // ~90.3%
  };
  
  // console.log('📍 Generated default percentage position:', defaultPercent);
  return defaultPercent;
};

/**
 * EXPORT-ONLY: Convert percentages back to PDF points
 * Used ONLY when exporting final PDF - not for daily operations!
 * 
 * @param {Object} percentPosition - Position as percentages {x, y}
 * @param {number} pageWidth - PDF page width in points
 * @param {number} pageHeight - PDF page height in points
 * @returns {Object} Position in PDF page units {x, y}
 */
export const percentagesToPdfPoints = (percentPosition, pageWidth, pageHeight) => {
  // console.log('📤 Converting Percentages → PDF points (EXPORT ONLY):', {
  // percentages: percentPosition,
  // pageSize: { width: pageWidth, height: pageHeight }
  // });

  // Convert percentages to CSS coordinates
  const cssLeft = (percentPosition.x / 100) * pageWidth;
  const cssTop = (percentPosition.y / 100) * pageHeight;
  
  // Convert CSS coordinate system (top-left origin) to PDF system (bottom-left origin)
  const pdfX = cssLeft;
  const pdfY = pageHeight - cssTop - STAMP_DIMENSIONS.BASE_HEIGHT;
  
  // Ensure position stays within PDF bounds
  const clampedPdfPoints = {
    x: Math.max(0, Math.min(pdfX, pageWidth - STAMP_DIMENSIONS.BASE_WIDTH)),
    y: Math.max(0, Math.min(pdfY, pageHeight - STAMP_DIMENSIONS.BASE_HEIGHT))
  };
  
  // console.log('✅ Percentages → PDF points conversion (for export):', clampedPdfPoints);
  return clampedPdfPoints;
};

/**
 * Validate percentage position is within bounds
 * 
 * @param {Object} percentPosition - Position as percentages {x, y}
 * @param {number} pageWidth - PDF page width in points (for calculating max percentages)
 * @param {number} pageHeight - PDF page height in points (for calculating max percentages)
 * @returns {Object} Validated percentage position {x, y}
 */
export const validatePercentagePosition = (percentPosition, pageWidth, pageHeight) => {
  const maxPercentX = 100 - (STAMP_DIMENSIONS.BASE_WIDTH / pageWidth * 100);
  const maxPercentY = 100 - (STAMP_DIMENSIONS.BASE_HEIGHT / pageHeight * 100);
  
  return {
    x: Math.max(0, Math.min(percentPosition.x, maxPercentX)),
    y: Math.max(0, Math.min(percentPosition.y, maxPercentY))
  };
};

/**
 * Get stamp dimensions as percentages of given page
 * 
 * @param {number} pageWidth - PDF page width in points
 * @param {number} pageHeight - PDF page height in points
 * @returns {Object} Stamp dimensions as percentages {width, height}
 */
export const getStampDimensionsPercent = (pageWidth, pageHeight) => {
  return {
    width: (STAMP_DIMENSIONS.BASE_WIDTH / pageWidth) * 100,
    height: (STAMP_DIMENSIONS.BASE_HEIGHT / pageHeight) * 100
  };
};

/**
 * Debug helper: Log coordinate system information
 */
/* export const debugCoordinateSystems = (percentPos, pdfDimensions) => {
  console.group('🐛 Coordinate Systems Debug');
  
  console.log('📊 Current percentage position:', percentPos);
  console.log('📄 PDF dimensions:', pdfDimensions);
  
  if (pdfDimensions) {
    const equivalentPdfPoints = percentagesToPdfPoints(percentPos, pdfDimensions.width, pdfDimensions.height);
    console.log('📤 Equivalent PDF points (if exported now):', equivalentPdfPoints);
    
    const stampSizePercent = getStampDimensionsPercent(pdfDimensions.width, pdfDimensions.height);
    console.log('📏 Stamp size as percentage:', stampSizePercent);
  }
  
  console.log('🎯 Stamp base dimensions (pixels):', STAMP_DIMENSIONS);
  
  console.groupEnd();
}; */